/**
 * Vue Router setup with lazy-loaded routes and auth guards.
 *
 * Groups are the app's home: an authenticated user lands on `/groups`, and the
 * group chat (`/groups/:id`) hosts both the chat and an inline voice panel —
 * there is no separate call route. Direct messages live in an overlay opened
 * from the app bar (see components/DmOverlay.vue), so there is no `/chat` route.
 * Admin user management is a modal opened from the app bar (AdminDialog), so
 * there is no `/admin` route either.
 *
 * Guards:
 *   - requireAuth:  if no access token in localStorage -> redirect to /login
 *   - guestOnly:    if already authenticated -> redirect to /groups
 */
import {
  createRouter,
  createWebHistory,
  type NavigationGuard,
  type RouteRecordRaw,
} from 'vue-router'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guestOnly?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: { name: 'groups' } },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { guestOnly: true },
  },
  {
    path: '/pending',
    name: 'pending',
    component: () => import('@/views/PendingView.vue'),
  },
  // Authenticated routes share the AppLayout shell (app bar + groups sidebar).
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'groups',
        name: 'groups',
        component: () => import('@/views/GroupsView.vue'),
      },
      {
        path: 'groups/:id',
        name: 'group-chat',
        component: () => import('@/views/GroupChatView.vue'),
        props: true,
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: { name: 'groups' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

/**
 * requireAuth — deny access when there is no persisted access token.
 * Exported so it can be attached per-route if needed; enforced globally below.
 */
export const requireAuth: NavigationGuard = (to) => {
  const auth = useAuthStore()
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  return true
}

router.beforeEach((to) => {
  const auth = useAuthStore()

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'groups' }
  }

  return true
})

export default router
